// =================================================================================
// File: src/components/Widgets/MudoMapWidget.jsx
// Description: Dashboard widget for Mudo Map using Leaflet
// =================================================================================

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../../lib/nav";
import { usePublicStudio } from "../../contexts/PublicStudioContext";
import { useStudio } from "../../contexts/StudioContext";
import { Globe, MapPin, Eye, EyeOff, Search, X } from "lucide-react";
import MudoWorldMap from "../Map/MudoWorldMap";
import {
  getNominatimHeaders,
  applyNominatimContact,
} from "../../utils/nominatim";
import WidgetHeader from "../Common/WidgetHeader";

export default function MudoMapWidget() {
  const navigate = useNavigate();
  const { publicProfile, isPublished, allPublicStudios = [], loading, publishStudio, unpublishStudio, refreshOwnProfile, refreshAllStudios } = usePublicStudio() || {};
  const { settings: studioSettings } = useStudio?.() || { settings: {} };

  const geocodeCacheRef = useRef(new Map());
  // Nominatim rate limit helper: 1 req/1100ms + single retry
  const nominatimLastAtRef = useRef(0);
  const nominatimHeaders = getNominatimHeaders();
  const nominatimFetch = async (url, { retry = true } = {}) => {
    const wait = Math.max(0, 1100 - (Date.now() - nominatimLastAtRef.current));
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    nominatimLastAtRef.current = Date.now();
    const resp = await fetch(url, { headers: nominatimHeaders });
    if ((resp.status === 429 || resp.status === 503) && retry) {
      await new Promise((r) => setTimeout(r, 1300));
      nominatimLastAtRef.current = Date.now();
      return nominatimFetch(url, { retry: false });
    }
    return resp;
  };
  const [resolvedStudios, setResolvedStudios] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchBoxRef = useRef(null);

  // Geocode studios to get coordinates
  useEffect(() => {
    let cancelled = false;
    async function geocode(city, country) {
      const key = `${city || ""}|${country || ""}`;
      if (!key.trim()) return null;
      if (geocodeCacheRef.current.has(key)) return geocodeCacheRef.current.get(key);
      try {
        const url = applyNominatimContact(
          new URL("https://nominatim.openstreetmap.org/search")
        );
        url.searchParams.set("q", [city, country].filter(Boolean).join(", "));
        url.searchParams.set("format", "json");
        url.searchParams.set("limit", "1");
        const resp = await nominatimFetch(url.toString());
        const data = await resp.json();
        const first = Array.isArray(data) && data[0];
        const pos = first ? { lat: Number(first.lat), lng: Number(first.lon) } : null;
        geocodeCacheRef.current.set(key, pos);
        return pos;
      } catch {
        geocodeCacheRef.current.set(key, null);
        return null;
      }
    }
    (async () => {
      const studios = (allPublicStudios || []).slice(0, 50);
      const withCoords = await Promise.all(studios.map(async (s) => {
        if (s.lat && s.lng) return s;
        if (!s.city && !s.country) return s;
        const pos = await geocode(s.city, s.country);
        return pos ? { ...s, ...pos } : s;
      }));
      if (!cancelled) setResolvedStudios(withCoords);
    })();
    return () => { cancelled = true; };
  }, [allPublicStudios]);

  // Merge freshest local profile into resolved list (in case network upsert is delayed/failed)
  const mergedStudios = React.useMemo(() => {
    const list = [...resolvedStudios];
    if (publicProfile) {
      const idx = list.findIndex(
        (s) => s.user_id && publicProfile.user_id && String(s.user_id) === String(publicProfile.user_id)
      );
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...publicProfile };
      } else {
        list.unshift(publicProfile);
      }
    }
    return list;
  }, [resolvedStudios, publicProfile]);

  // Convert to format MudoWorldMap expects
  const mapLocations = mergedStudios
    .filter(s => s.lat && s.lng)
    .map(studio => ({
      id: studio.id || studio.user_id,
      name: studio.display_name || "Studio",
      type: "studio",
      lat: studio.lat,
      lng: studio.lng,
      address: (studio.address || "").trim(),
      description: studio.address 
        ? "" // If we have an address, don't show city/country as description
        : [studio.city, studio.country].filter(Boolean).join(", "),
    }));

  // Handle location click from map
  const handleLocationClick = (location) => {
    // Find the original studio data
    const studio = resolvedStudios.find(s => 
      (s.id === location.id || s.user_id === location.id)
    );
    
    if (studio) {
      const url = studio.booking_url || studio.website;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        navigateTo(navigate, ROUTES.EXPLORE, { source: 'MudoMapWidget:studioClick' });
      }
    } else {
      navigateTo(navigate, ROUTES.EXPLORE, { source: 'MudoMapWidget:studioClick' });
    }
  };

  // Search location handler
  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const url = new URL("https://nominatim.openstreetmap.org/search");
      url.searchParams.set("q", query);
      url.searchParams.set("format", "json");
      url.searchParams.set("limit", "5");
      const resp = await nominatimFetch(url.toString());
      const data = await resp.json();
      setSearchResults(data || []);
    } catch (err) {
      console.error("Search failed:", err);
      setSearchResults([]);
    }
  };

  // Note: flyToLocation is now handled by the search results clicking directly
  // The map will auto-zoom based on search result selection

  // Close search dropdown on outside click / ESC
  useEffect(() => {
    const onDocClick = (e) => {
      if (!searchBoxRef.current) return;
      if (!searchBoxRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSearchQuery("");
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Quick publish directly from widget
  const [actionBusy, setActionBusy] = useState(false);
  const openMapPicker = () => {
    try {
      window.dispatchEvent?.(
        new CustomEvent("mudo:open-customize-modal", {
          detail: { tab: "public", mapPicker: true },
        })
      );
    } catch (e) {
      console.warn("[MudoMapWidget] Failed to open map picker", e);
    }
  };
  const handleQuickPublish = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      openMapPicker();
    } catch (e) {
      console.error("[MudoMapWidget] Quick publish failed", e);
    } finally {
      setActionBusy(false);
    }
  };

  const handleQuickUnpublish = async () => {
    if (actionBusy || !unpublishStudio) return;
    setActionBusy(true);
    try {
      console.log("[MudoMapWidget] Unpublishing studio");
      await unpublishStudio();
      refreshOwnProfile?.();
      refreshAllStudios?.();
    } catch (e) {
      console.error("[MudoMapWidget] Unpublish failed", e);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      {/* Header */}
      <WidgetHeader
        title="Mudo Map"
        right={
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
            Studios: {allPublicStudios?.length || 0}
          </span>
        }
        className="mb-3"
      />
      <div className="text-xs opacity-70 mb-2">Hover pins to see studio. Click to book.</div>

      {loading ? (
        <div className="py-8 text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm opacity-70">Loading...</p>
        </div>
      ) : (
        <>
          {/* Search Location */}
          <div className="mb-3 relative" ref={searchBoxRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                placeholder="Search location..."
                className="w-full pl-9 pr-9 py-2 rounded-xl bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-blue-500/50 focus:bg-white/10"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults([]);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {searchResults.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-zinc-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 max-h-48 overflow-y-auto">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      // Add searched location as a temporary pin
                      const tempLocation = {
                        id: `search-${idx}`,
                        name: result.display_name,
                        type: "other",
                        lat: parseFloat(result.lat),
                        lng: parseFloat(result.lon),
                        description: "Search result"
                      };
                      // You could optionally add this to mapLocations if you want to show it
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-white/5 border-b border-white/5 last:border-b-0"
                  >
                    <div className="text-xs font-medium truncate">{result.display_name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Leaflet Map */}
          <div className="mb-4">
            <div className="relative w-full h-56 rounded-xl overflow-hidden border border-white/10">
              <MudoWorldMap
                locations={mapLocations}
                initialCenter={[20, 0]}
                initialZoom={2}
                onLocationClick={handleLocationClick}
                worldMode="studio"
              />
            </div>
          </div>

          {/* Status */}
          <div className="mb-4 p-3 rounded-xl border border-white/10 bg-white/5">
            <div className="flex items-center gap-2 mb-1">
              {isPublished ? (
                <>
                  <Eye className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-green-400">Visible on map</span>
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm font-medium text-zinc-400">Not visible</span>
                </>
              )}
            </div>
            <p className="text-xs opacity-70">
              {isPublished
                ? "Your studio appears on the global Mudo Map for viewers to discover."
                : "Publish your studio to appear on the global map and reach more people."}
            </p>
          </div>

          {/* Location */}
          {publicProfile && (
            <div className="mb-4 p-3 rounded-xl border border-white/10">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{publicProfile.display_name || "My Studio"}</div>
                  {publicProfile.address ? (
                    <div className="text-xs opacity-70 mt-1">{publicProfile.address}</div>
                  ) : (
                    <div className="text-xs opacity-70">
                      {publicProfile.city && `${publicProfile.city}, `}
                      {publicProfile.country || "Location not set"}
                    </div>
                  )}
                  {publicProfile.lat && publicProfile.lng && (
                    <div className="text-[10px] opacity-50 mt-1">
                      📍 {publicProfile.lat.toFixed(4)}, {publicProfile.lng.toFixed(4)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            <button
              onClick={() => navigateTo(navigate, ROUTES.EXPLORE, { source: 'MudoMapWidget:openGlobalMap' })}
              className="w-full py-2 px-4 rounded-xl bg-blue-500/90 hover:bg-blue-500 text-white font-medium text-sm transition-colors"
            >
              Open Global Map
            </button>
            <div className="flex gap-2">
              {!isPublished && (
                <button
                  disabled={actionBusy}
                  onClick={handleQuickPublish}
                  className="flex-1 py-2 px-4 rounded-xl border border-blue-500/40 bg-blue-500/20 hover:bg-blue-500/30 text-sm font-medium transition-colors disabled:opacity-40"
                >
                  {actionBusy ? "Publishing..." : "Publish to Map"}
                </button>
              )}
              {isPublished && (
                <button
                  disabled={actionBusy}
                  onClick={handleQuickUnpublish}
                  className="flex-1 py-2 px-4 rounded-xl border border-red-500/40 bg-red-500/20 hover:bg-red-500/30 text-sm font-medium transition-colors disabled:opacity-40"
                >
                  {actionBusy ? "Updating..." : "Unpublish"}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
